import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Category,
  Document,
  DocumentUploadOptions,
  UploadDocumentItem,
  DocumentDetail,
  CategoryInfo,
  SignOptions,
  UploadSignMetadata,
  UploadableDocumentTypes,
  DocumentTypeSelectorOptions,
} from '../models/documents.type';
import { X_HOHR_CAPTCHA_TOKEN } from '@app/core/constants/recaptcha.constant';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';
import { OnboardingSteps } from '@app/onboarding/models/onboarding.type';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  http = inject(HttpClient);
  toastSandbox = inject(ToastSandbox);

  baseUrl = `${environment.url.api}/document`;

  getUploadableDocumentTypes(): Observable<UploadableDocumentTypes> {
    return this.http.get<UploadableDocumentTypes>(`${this.baseUrl}/options/upload`);
  }

  getCategory(): Observable<{ categories: Category[]; portal: { url: string } | null }> {
    return this.http.get<{ categories: Category[]; portal: { url: string } | null }>(`${this.baseUrl}/category`);
  }

  getCategoryInfoById(categoryId: string): Observable<CategoryInfo> {
    return this.http
      .get<{ category: CategoryInfo }>(`${this.baseUrl}/category/${categoryId}`)
      .pipe(map((x) => x.category));
  }

  getSubItemsOfCategoryById(categoryId: string): Observable<{ categories: Category[] }> {
    return this.http.get<{ categories: Category[] }>(`${this.baseUrl}/category?categoryId=${categoryId}`);
  }

  getDocumentsByCategoryId(categoryId: string): Observable<{ documents: Document[] }> {
    return this.http.get<{ documents: Document[] }>(`${this.baseUrl}/category/${categoryId}/documents`);
  }

  getDocumentUploadOptions(documentTypeId: string): Observable<DocumentUploadOptions> {
    return this.http.get<DocumentUploadOptions>(`${this.baseUrl}/documenttype/${documentTypeId}/options/upload`);
  }

  getDocumentSelectorOptions(documentTypeId: string): Observable<DocumentTypeSelectorOptions> {
    return this.http.get<DocumentTypeSelectorOptions>(
      `${this.baseUrl}/documenttype/${documentTypeId}/options/selector`
    );
  }

  getDocumentDetail(documentId: string): Observable<DocumentDetail> {
    return this.http.get<DocumentDetail>(`${this.baseUrl}/${documentId}`);
  }

  uploadDocumentTypeDocument(documentTypeId: string, document: UploadDocumentItem): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/documenttype/${documentTypeId}/upload`, document);
  }

  startVerification(documentId: string, captchaToken: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${documentId}/sign/start`, 
      null,
      { headers: { [X_HOHR_CAPTCHA_TOKEN]: captchaToken } });
  }

  approveDocument(documentId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${documentId}/approve`, null);
  }

  disapproveDocument(documentId: string, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${documentId}/disapprove`, { reason });
  }

  signDocument(documentId: string, verificationCode: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${documentId}/sign/verify`, { verificationCode });
  }

  refuseDocument(documentId: string, verificationCode: string, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${documentId}/sign/refuse`, { verificationCode, reason });
  }

  downloadDocument(documentId: string): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${documentId}/download`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((response) => response.body));
  }

  getSignOptions(documentId: string): Observable<SignOptions> {
    return this.http.get<SignOptions>(`${this.baseUrl}/${documentId}/sign/options`);
  }

  uploadSignMetadata(documentId: string, metadata: UploadSignMetadata): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${documentId}/sign/metaData`, metadata);
  }

  getOnboardingDocuments(step: OnboardingSteps): Observable<Document[]> {
    return this.http.get<{ documents: Document[]}>(`${this.baseUrl}/onboarding?onboardingStepName=${step}`)
      .pipe(map(response => response.documents));
  }

  downloadLatestPayslip(): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/me/payslip/latest`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return this.createBlob(response);
          } else if (response.status === 204) {
            this.toastSandbox.showErrorToast({key: 'my-work.latest-payslip.empty'});
          }
          return null;
        })
      );
  }

  downloadLatestInvoice(): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/me/invoice/latest`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return this.createBlob(response);
          } else if (response.status === 204) {
            this.toastSandbox.showToast({key: 'my-work.latest-invoice.empty'});
          }
          return null;
        })
      );
  }

  private createBlob(response): Blob {
    const extract = response?.headers?.headers?.get('content-type');
    const contentType = extract ? extract[0] : 'application/octet-stream';
    return new Blob([response.body], { type: contentType });
  }
}
